<template>
  <v-row justify="end" class="ma-0">
    <v-col cols="12" sm="7" md="6">
      <!-- Subtotal price -->
      <v-row
        v-if="isProfUser && hasProductsInList"
        justify="space-between"
        class="ma-0 text-subtitle-2"
      >
        <div>{{ $translate('SUBTOTAL') }}</div>
        <div>
          {{ formatter.format(getSubtotalPrice(item)) }}
        </div>
      </v-row>

      <!-- Delivery fee -->
      <v-row
        v-if="isDeliveryDetailsValid(item) && hasProductsInList"
        justify="space-between"
        class="ma-0 text-subtitle-2"
      >
        <div>{{ $translate('DELIVERY_FEE') }}</div>
        <div>
          {{ formatter.format(getListDeliveryPrice(item)) }}
        </div>
      </v-row>

      <!-- Pickup fee (getPickupFee doesn't get proper data from BE. It is always zero.)-->
      <v-row
        v-if="showPickupPrice"
        justify="space-between"
        class="ma-0 text-subtitle-2"
      >
        <div>{{ $translate('PICKUP_FEE') }}</div>
        <div>
          {{ formatter.format(pickupPrice) }}
        </div>
      </v-row>

      <!-- Discount -->
      <v-row
        v-if="Number(getTotalDiscount(item)).toFixed(2) < 0"
        justify="space-between"
        class="ma-0 text-subtitle-2"
      >
        <div>{{ $translate('DISCOUNT') }}</div>
        <div class="red--text">
          {{ formatter.format(getTotalDiscount(item)) }}
        </div>
      </v-row>

      <!-- deposits -->
      <v-row justify="space-between" class="ma-0 text-subtitle-2" v-if="getDepositValue(item) > 0">
        <div>{{ $translate('DEPOSIT_VALUE') }}</div>
        <div>{{ formatter.format(getDepositValue(item)) }}</div>
      </v-row>

      <!-- VAT info for the professional user -->
      <v-row
        v-if="isProfUser && hasProductsInList"
        justify="space-between"
        class="ma-0 text-subtitle-2"
      >
        <div>{{ $translate('VAT') }}</div>
        <div>
          {{ formatter.format(getTotalVATPrice(item)) }}
        </div>
      </v-row>

      <!-- Total price -->
      <v-row
        v-if="hasProductsInList"
        justify="space-between"
        class="ma-0 text-h6"
      >
        <div>{{ $translate('LINDA_TOTAL_PRICE') }}</div>
        <div>
          {{ formatter.format(getShoppingListTotalPrice(item)) }}
        </div>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import utility from '@/shared/utility';
import { isWeightedProduct } from '@/objects/Product';
import DeliveryFee from '@/objects/DeliveryFee';
import PickupFeeJS from '@/objects/PickupFee';

export default {
  name: 'ShoppingListTotalPrice',
  props: {
    selectedShoppingList: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
      item: {},
      hasProductsInList: false,
      pickupFee: null,
      minPickupFeeAlert: '',
    };
  },
  watch: {
    selectedShoppingList() {
      this.init();
    },
  },
  mounted() {
    this.init();
  },
  computed: {
    ...mapGetters({
      isProfUser: 'isProfUser',
    }),
    showPickupPrice() {
      return (
        this.pickupFee != null &&
        this.pickupFee.isPossible() &&
        this.pickupFee.hasPrice()
      );
    },
    pickupPrice() {
      if (!this.showPickupPrice) {
        return 0.0;
      }

      if (this.isProfUser) {
        return this.pickupFee.price - this.pickupFee.vatPrice;
      }
      return this.pickupFee.price;
    },
  },
  methods: {
    init() {
      if (Object.keys(this.selectedShoppingList).length > 0) {
        this.item = cloneDeep(this.selectedShoppingList);
        this.hasProductsInList = this.item.Products.length > 0;
        console.log('item', this.hasProductsInList, this.item);
      }
      this.getDeliveryFee();
      this.getPickupFee();
      // this.initPaymentMethod();
      //  this.getAwardedGrowthVitaminCardsForList();
    },
    getSubtotalPrice(list) {
      let price = 0;

      switch (list.Type) {
        case 'WishList':
          list.Products.forEach((product) => {
            price += this.getProductRetailPriceExclVat(product);
            console.log('SubTotal product retail price', price);
          });
          break;
        default:
          if (list.OrderGroup != null && list.OrderGroup.Orders != null) {
            list.OrderGroup.Orders.forEach((order) => {
              order.Products.forEach((product) => {
                let amount = product.Quantity;
                if (isWeightedProduct(product)) {
                  amount = product.Quantity / product.StepSize;
                }

                price +=
                  amount *
                  (product.ItemRetailPrice - product.ItemRetailVatPrice);
              });
            });
          }
          break;
      }
      return price;
    },
    isDeliveryDetailsValid(shoppingList) {
      return (
        shoppingList != null &&
        shoppingList.SubEventType != null &&
        (shoppingList.SubEventType === 'Delivery' ||
          shoppingList.SubEventType === 'Shiplee') &&
        shoppingList.DeliveryDetails != null &&
        shoppingList.DeliveryDetails.ResultSet != null &&
        shoppingList.DeliveryDetails.ResultSet.Price != null
      );
    },
    async getDeliveryFee() {
      try {
        let a = new DeliveryFee();
        const response = await a.getDeliveryFee(this.item);
        if (response != null) this.onGetDeliveryFeeResponse(response);
      } catch (error) {
        this.onGetDeliveryFeeError();
        console.log('ERROR', error);
      }
    },
    onGetDeliveryFeeResponse(response) {
      const deliveryDetails = { ...response.data };

      const deliveryInfo = {};
      if (response.data.DeliveryPossible) {
        deliveryInfo.DeliveryText = `${this.$translate(
          'DELIVERY_FEE'
        )}: ${this.formatter.format(response.data.Price)}`;
        deliveryInfo.DeliveryError = false;
        deliveryInfo.Price = response.data.Price;
      } else {
        if (
          response.data.ResultSet != null &&
          response.data.ResultSet.AdditonalOrderValueForDelivery != null
        ) {
          deliveryInfo.DeliveryText = this.$translate(
            'DELIVERY_NO_ENOUGH_PRODUCTS'
          ).replace(
            'price',
            this.formatter.format(
              response.data.ResultSet.AdditonalOrderValueForDelivery
            )
          );
        } else {
          deliveryInfo.DeliveryText = this.$translate(
            'NO_DELIVERY_FOR_POSTAL_CODE'
          );
        }
        deliveryInfo.DeliveryError = true;
      }

      this.$set(this.item, 'DeliveryFee', deliveryInfo);
      this.$set(this.item, 'DeliveryDetails', deliveryDetails);
    },
    onGetDeliveryFeeError() {
      const deliveryInfo = {};
      deliveryInfo.DeliveryText = this.$translate(
        'NO_DELIVERY_FOR_POSTAL_CODE'
      );
      deliveryInfo.DeliveryError = true;

      this.$set(this.item, 'DeliveryFee', deliveryInfo);
    },
    getListDeliveryPrice(shoppingList) {
      if (!this.isDeliveryDetailsValid(shoppingList)) {
        return 0;
      }

      if (this.isProfUser) {
        return this.getListDeliveryPriceExclVAT(shoppingList);
      }

      return this.getListDeliveryPriceInclVAT(shoppingList);
    },
    getListDeliveryPriceInclVAT(shoppingList) {
      if (!this.isDeliveryDetailsValid(shoppingList)) {
        return 0;
      }
      return shoppingList.DeliveryDetails.ResultSet.Price;
    },
    getListDeliveryPriceExclVAT(shoppingList) {
      if (!this.isDeliveryDetailsValid(shoppingList)) {
        return 0;
      }

      const deliveryDetails = shoppingList.DeliveryDetails;
      return (
        deliveryDetails.ResultSet.Price - deliveryDetails.ResultSet.VatPrice
      );
    },
    getListDeliveryVATPrice(shoppingList) {
      if (!this.isDeliveryDetailsValid(shoppingList)) {
        return 0;
      }
      return shoppingList.DeliveryDetails.ResultSet.VatPrice;
    },
    getPickupFee() {
      this.pickupFee = null;
      this.minPickupFeeAlert = '';

      const list = this.item;
      if (list.Temporary || list.SubEventType !== 'PickUp') {
        return;
      }

      this.pickupFee = new PickupFeeJS(list.SubEventId);
      this.pickupFee.load({}, list.ShoppingListId).then(() => {
        this.minPickupFeeAlert = this.pickupFee.alertText();
      });
      console.log('pickup price', this.pickupFee);
    },
    // get discount amount
    getTotalDiscount(list) {
      let totalDiscountPrice = 0;

      switch (list.Type) {
        case 'WishList':
          list.Products.forEach((product) => {
            if (this.isProfUser) {
              totalDiscountPrice +=
                product.PriceExclVat -
                this.getProductRetailPriceExclVat(product);
            } else {
              totalDiscountPrice +=
                product.Price - this.getProductRetailPriceInclVat(product);
            }
          });
          break;
        default:
          if (list.OrderGroup != null && list.OrderGroup.Orders != null) {
            list.OrderGroup.Orders.forEach((order) => {
              // Discounts from growth vitamin cards
              order.Discounts.forEach((discount) => {
                if (this.isProfUser) {
                  totalDiscountPrice += discount.Price - discount.VatPrice;
                } else {
                  totalDiscountPrice += discount.Price;
                }
              });

              // Discounts from products
              order.Products.forEach((product) => {
                let amount = product.Quantity;
                if (isWeightedProduct(product)) {
                  amount = product.Quantity / product.StepSize;
                }

                if (this.isProfUser) {
                  let totalRetailPriceExclVat =
                    amount *
                    (product.ItemRetailPrice - product.ItemRetailVatPrice);
                  totalDiscountPrice +=
                    product.ItemTotalPrice -
                    product.ItemTotalVatPrice -
                    totalRetailPriceExclVat;
                } else {
                  let totalRetailPriceInclVat =
                    amount * product.ItemRetailPrice;
                  totalDiscountPrice +=
                    product.ItemTotalPrice - totalRetailPriceInclVat;
                }
              });
            });
          }

          break;
      }

      if (totalDiscountPrice > 0) {
        totalDiscountPrice = 0;
      }

      return totalDiscountPrice;
    },
    getProductRetailPriceInclVat(product) {
      let price = product.RetailPrice;
      if (price === 0) {
        price = product.Price;
      }
      return price;
    },
    getProductRetailPriceExclVat(product) {
      let price = product.RetailPriceExVat;
      if (price === 0) {
        price = product.PriceExclVat;
      }
      return price;
    },
    getTotalVATPrice(list) {
      // Total vat price includes vat of products and delivery
      let totalVATPrice = 0;

      switch (list.Type) {
        case 'WishList':
          list.Products.forEach((product) => {
            const vatPrice = utility.getVatPrice(
              product.Price,
              product.Product.VatPercentage
            );
            totalVATPrice += vatPrice;
          });
          break;
        default:
          if (list.OrderGroup != null && list.OrderGroup.Orders != null) {
            list.OrderGroup.Orders.forEach((order) => {
              // Vat from growth vitamin cards
              order.Discounts.forEach((discount) => {
                totalVATPrice += discount.VatPrice;
              });

              // Vat from products
              order.Products.forEach((product) => {
                totalVATPrice += product.ItemTotalVatPrice;
              });
            });
          }
          break;
      }

      totalVATPrice += this.getListDeliveryVATPrice(list);

      if (this.pickupFee != null && this.pickupFee.isPossible()) {
        totalVATPrice += this.pickupFee.vatPrice;
      }

      return totalVATPrice;
    },
    getShoppingListTotalPrice(shoppingList) {
      let totalPrice = 0;

      switch (shoppingList.Type) {
        case 'WishList':
          shoppingList.Products.forEach((product) => {
            totalPrice += product.Price + product.TotalDepositValue;
          });
          break;
        default:
          totalPrice = shoppingList.OrderGroup.TotalPrice;
          break;
      }

      totalPrice += this.getListDeliveryPriceInclVAT(shoppingList);

      if (this.pickupFee != null && this.pickupFee.isPossible()) {
        totalPrice += this.pickupFee.price;
      }

      return totalPrice;
    },
    getDepositValue(shoppingList) {
      let deposit = 0.0
      switch(shoppingList.Type) {
        case 'WishList':
          shoppingList.Products.forEach((product) => {
            deposit += product.TotalDepositValue
          });
          break;
        default:
          if(shoppingList.hasOwnProperty('OrderGroup') && shoppingList.OrderGroup !== null) {
            deposit = shoppingList.OrderGroup.TotalDepositValue;
          }
      }
      return deposit
    }
  },
};
</script>
<style scoped lang="scss"></style>
